/**
 * Multi-tenant identity & authorization invariants (Initiative SoT helpers).
 *
 * These pure helpers document and enforce the target authority model:
 *   organization_members.role  → authoritative tenant role
 *   users.role                 → temporary compatibility mirror only
 *
 * Do not use users.role as an authorization authority in new code.
 */

import { MEMBERSHIP_ROLE, mapMembershipRoleToLegacyRole } from "./membershipHelpers";

export const LEGACY_APP_ROLE = Object.freeze({
  ADMIN: "admin",
  MANAGER: "manager",
  EMPLOYEE: "employee",
  GUEST: "guest",
});

/** Map Admin UI / legacy role values onto membership roles (never invents owner). */
export function mapLegacyRoleToMembershipRole(legacyRole) {
  switch (legacyRole) {
    case LEGACY_APP_ROLE.ADMIN:
      return MEMBERSHIP_ROLE.ADMIN;
    case LEGACY_APP_ROLE.MANAGER:
      return MEMBERSHIP_ROLE.MANAGER;
    case LEGACY_APP_ROLE.GUEST:
      return MEMBERSHIP_ROLE.GUEST;
    case LEGACY_APP_ROLE.EMPLOYEE:
    default:
      return MEMBERSHIP_ROLE.EMPLOYEE;
  }
}

/**
 * Protect organization ownership when Administration edits a membership role.
 * Returns the role that should be persisted on organization_members.
 *
 * - Never silently demotes owner → admin/manager/employee.
 * - Never promotes to owner via the ordinary Admin role dropdown.
 */
export function resolveMembershipRoleUpdate({
  currentMembershipRole,
  requestedLegacyRole,
  allowOwnerDemotion = false,
  allowOwnerPromotion = false,
} = {}) {
  const current = currentMembershipRole || MEMBERSHIP_ROLE.EMPLOYEE;

  if (current === MEMBERSHIP_ROLE.OWNER) {
    if (allowOwnerDemotion && requestedLegacyRole) {
      return mapLegacyRoleToMembershipRole(requestedLegacyRole);
    }
    return MEMBERSHIP_ROLE.OWNER;
  }

  const next = mapLegacyRoleToMembershipRole(requestedLegacyRole);

  if (next === MEMBERSHIP_ROLE.OWNER && !allowOwnerPromotion) {
    return MEMBERSHIP_ROLE.ADMIN;
  }

  // Ordinary Admin UI cannot create owners.
  if (requestedLegacyRole === MEMBERSHIP_ROLE.OWNER && !allowOwnerPromotion) {
    return MEMBERSHIP_ROLE.ADMIN;
  }

  return next;
}

export function detectRoleDrift(membershipRole, legacyUsersRole) {
  if (!membershipRole && !legacyUsersRole) {
    return null;
  }

  const expectedLegacy = mapMembershipRoleToLegacyRole(membershipRole);
  if (!legacyUsersRole) {
    return {
      kind: "missing_legacy",
      membershipRole,
      legacyUsersRole: null,
      expectedLegacy,
    };
  }

  if (expectedLegacy !== legacyUsersRole) {
    return {
      kind: "mismatch",
      membershipRole,
      legacyUsersRole,
      expectedLegacy,
    };
  }

  return null;
}

/**
 * Whether two enriched actors for the same auth identity represent isolated
 * organization authorities (no cross-org role leak in the actor payload).
 */
export function assertMultiOrgRoleIsolation(actorOrgA, actorOrgB) {
  if (!actorOrgA || !actorOrgB) {
    return { ok: false, reason: "missing_actor" };
  }

  if (actorOrgA.supabase_user_id !== actorOrgB.supabase_user_id) {
    return { ok: false, reason: "different_identities" };
  }

  if (actorOrgA.organization_id === actorOrgB.organization_id) {
    return { ok: false, reason: "same_organization" };
  }

  // Roles may differ across orgs — that is expected and required.
  return {
    ok: true,
    orgA: {
      organization_id: actorOrgA.organization_id,
      membership_role: actorOrgA.membership_role,
      role: actorOrgA.role,
    },
    orgB: {
      organization_id: actorOrgB.organization_id,
      membership_role: actorOrgB.membership_role,
      role: actorOrgB.role,
    },
    rolesDiffer:
      actorOrgA.membership_role !== actorOrgB.membership_role ||
      actorOrgA.role !== actorOrgB.role,
  };
}

export function isOwnerMembershipRole(role) {
  return role === MEMBERSHIP_ROLE.OWNER;
}

export function ownershipPointerConsistent({
  membershipId,
  membershipRole,
  ownerMemberId,
} = {}) {
  if (!membershipId) {
    return false;
  }

  const isOwner = membershipRole === MEMBERSHIP_ROLE.OWNER;
  const pointed = ownerMemberId === membershipId;

  if (isOwner && pointed) {
    return true;
  }

  if (!isOwner && !pointed) {
    return true;
  }

  // Owner without pointer, or pointer to non-owner — inconsistent.
  return false;
}
